import React, { useEffect, useRef, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Flex, message } from 'antd';
import { CustomButton, CustomModal, CustomParagraph } from '../../components';
import { uploadFile } from '../../apis/shipment';
import { useSpin } from '../../components/SpinContext';
import styles from './FileUploadModal.module.css';

interface FileUploadModalProps {
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  shipmentId: string;
  token: string;
  fetchSingleShipmentData: () => void;
}

/**
 * File upload modal component for shipments
 *
 * Allows users to upload files via click or drag-and-drop.
 * Displays file information, handles upload to server, and manages loading states.
 */
const FileUploadModal: React.FC<FileUploadModalProps> = ({
  modalOpen,
  setModalOpen,
  shipmentId,
  token,
  fetchSingleShipmentData,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showSpin, hideSpin } = useSpin();

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
    event.target.value = '';
  };

  const openFileDialog = (event: React.MouseEvent) => {
    event.stopPropagation();
    fileInputRef.current?.click();
  };

  const removeFile = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedFile(null);
  };

  const unauthorize = (error: { status?: number }) => {
    if (error?.status === 401) {
      setModalOpen(false);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    if (!shipmentId) {
      message.error('Shipment ID is missing!');
      return;
    }

    const formData = new FormData();
    formData.append('shipment_id', shipmentId);
    formData.append('file', selectedFile);

    showSpin();
    uploadFile(token, formData)
      .then((res) => {
        const data = res?.data?.data || null;
        if (data?.is_success) {
          message.success(data?.message);
          fetchSingleShipmentData();
          setModalOpen(false);
          setSelectedFile(null);
        } else {
          message.error(data?.message);
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  };

  useEffect(() => {
    if (!modalOpen) {
      setSelectedFile(null);
    }
  }, [modalOpen]);

  return (
    <CustomModal
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      modalTitle='Upload file'
      modalSize={500}
      buttonText1={selectedFile ? 'Cancel' : undefined}
      buttonText2={selectedFile ? 'Upload' : undefined}
      isFormValid={!selectedFile}
      onConfirmClick={handleUpload}
    >
      <div
        className={selectedFile ? styles.selected : styles.dropZone}
        onClick={openFileDialog}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (event.dataTransfer.files.length > 0) {
            setSelectedFile(event.dataTransfer.files[0]);
          }
        }}
      >
        {!selectedFile ? (
          <>
            <CustomParagraph
              text='Drop file here to upload...'
              fontSize='12px'
              fontWeight='400'
              color='var(--text-light)'
              textAlign='center'
              marginBottom='10px'
            />
            <CustomButton
              text='Browse files'
              width='120px'
              height='36px'
              fontSize='12px'
              fontWeight='600'
              borderRadius='20px'
              bgColor='var(--light-gray)'
              color='var(--text-primary)'
              boxShadow='none'
              onClick={openFileDialog}
              iconPosition='left'
              gap={8}
            />
          </>
        ) : (
          <Flex
            onClick={(event) => event.stopPropagation()}
            align='center'
            justify='space-between'
            className={styles.file}
          >
            <div>
              <CustomParagraph
                text={
                  selectedFile.name.length > 40
                    ? `${selectedFile.name.substring(0, 40)}...`
                    : selectedFile.name
                }
                fontSize='14px'
                fontWeight='600'
                color='var(--text-primary)'
                textAlign='left'
                marginBottom='4px'
              />
              <CustomParagraph
                text={`${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`}
                fontSize='12px'
                fontWeight='400'
                color='var(--text-light)'
                textAlign='left'
              />
            </div>
            <span title='Close' className='no-bg' onClick={removeFile}>
              <CloseOutlined color='var(--text-primary)' />
            </span>
          </Flex>
        )}
      </div>

      <input
        type='file'
        ref={fileInputRef}
        hidden
        onChange={handleFileSelect}
      />
    </CustomModal>
  );
};

export default FileUploadModal;
